import { useState, useEffect } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "./ui/dialog";
import { toast } from "sonner";
import { FileScan, Receipt, Loader2, ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import StepDocument from "./intake/StepDocument";
import StepProducts from "./intake/StepProducts";
import LabelCapture from "./intake/LabelCapture";
import StepSummary from "./intake/StepSummary";
import BarcodeScanner from "./intake/BarcodeScanner";
import { newItem, newLot, mergeItems, traceStatus, similarity, compressImage } from "./intake/intakeUtils";

const EMPTY_DOC = { supplier: "", vat_number: "", doc_type: "fattura", doc_number: "", date: "", delivery_date: "", taxable: "", tax: "", total: "", payment_terms: "", receipt_path: "", receipt_paths: [] };
const STEPS = ["Documento", "Prodotti", "Etichette", "Riepilogo"];

export default function InvoiceIntake({ onDone, defaultAccounting = true, docType = "fattura" }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [doc, setDoc] = useState({ ...EMPTY_DOC, doc_type: docType });
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [quickScanning, setQuickScanning] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [capIdx, setCapIdx] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanHighlight, setScanHighlight] = useState(null);
  const [opts, setOpts] = useState({ inv: true, acc: defaultAccounting, haccp: true, payMethod: "contante", ledger: "ufficiale", taxRate: 22, conformity: "conforme" });
  const [maxStep, setMaxStep] = useState(0);
  const go = (s) => { setStep(s); setMaxStep((m) => Math.max(m, s)); };
  const token = localStorage.getItem("token");

  useEffect(() => { if (open && !classes.length) api.get("/intake/trace-classes").then(({ data }) => setClasses(data)).catch(() => {}); }, [open, classes.length]);

  const reset = () => { setStep(0); setMaxStep(0); setDoc({ ...EMPTY_DOC, doc_type: docType }); setItems([]); setCapIdx(null); setScanning(false); setCommitting(false); };

  const scanFiles = async (files) => {
    if (!files?.length) return;
    setScanning(true);
    try {
      const d = { ...doc, receipt_paths: [...(doc.receipt_paths || [])] };
      let added = [];
      for (const f0 of Array.from(files)) {
        const f = await compressImage(f0);
        const fd = new FormData(); fd.append("file", f);
        const { data: r } = await api.post("/ai/scan-invoice", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ["supplier", "vat_number", "doc_number", "date", "delivery_date", "payment_terms"].forEach((k) => { if (!d[k] && r[k]) d[k] = r[k]; });
        ["taxable", "tax", "total"].forEach((k) => { if (!d[k] && r[k]) d[k] = r[k]; });
        if (r.doc_type && docType === "fattura") d.doc_type = r.doc_type;
        if (r.receipt_path) { d.receipt_paths.push(r.receipt_path); if (!d.receipt_path) d.receipt_path = r.receipt_path; }
        added = [...added, ...(r.items || []).map(newItem)];
      }
      const all = mergeItems([...items, ...added]);
      setDoc(d); setItems(all);
      if (!all.length) toast.message("Nessun articolo rilevato: puoi aggiungerli a mano");
      else { toast.success(`${all.length} prodotti rilevati nel documento`); go(1); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setScanning(false); }
  };

  const needIdx = items.map((it, i) => (it._sel && it.trace_required ? i : -1)).filter((i) => i >= 0);
  const nextNeeding = (from) => needIdx.find((i) => i > from && traceStatus(items[i]) !== "completa");

  const openCapture = (i) => { setCapIdx(i); go(2); };
  const goNextProduct = () => { const n = nextNeeding(capIdx ?? -1); if (n !== undefined) setCapIdx(n); else { setCapIdx(null); go(3); toast.success("Tutti i prodotti tracciati"); } };

  const quickScan = async (files) => {
    if (!files?.length) return;
    setQuickScanning(true);
    let assigned = 0, unassigned = 0;
    const next = items.map((it) => ({ ...it, lots: [...(it.lots || [])] }));
    try {
      for (const f0 of Array.from(files)) {
        const f = await compressImage(f0);
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/ai/read-label", fd, { headers: { "Content-Type": "multipart/form-data" } });
        let best = -1, score = 0;
        next.forEach((it, i) => {
          if (!it._sel || !it.trace_required) return;
          let s = similarity(it.name, `${data.product_name} ${data.brand}`);
          if (data.barcode && it.barcode && data.barcode === it.barcode) s = 1;
          if (s > score) { score = s; best = i; }
        });
        const lot = newLot({ ...data, quantity: 0, ai_data: data, match_warning: score < 0.34 });
        if (best >= 0 && score >= 0.2) { const it = next[best]; lot.quantity = it.lots.length ? 0 : it.quantity; it.lots.push(lot); assigned++; }
        else { unassigned++; const it = newItem({ name: data.product_name || "Etichetta non abbinata", quantity: 0, trace_class: "confezionato" }); it.lots = [lot]; next.push(it); }
      }
      setItems(next);
      toast.success(`Scansione rapida: ${assigned} etichette abbinate${unassigned ? `, ${unassigned} da abbinare a mano` : ""}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setQuickScanning(false); }
  };

  const onBarcodeScan = async (code) => {
    const c = (code || "").replace(/\D/g, "");
    const idx = items.findIndex((it) => (it.barcode || "").replace(/\D/g, "") === c && c);
    if (idx >= 0) {
      setItems(items.map((it, k) => (k === idx ? { ...it, _sel: true } : it)));
      setScanHighlight(idx);
      setScanResult({ ok: true, msg: `Trovato: ${items[idx].name}` });
      setTimeout(() => { document.getElementById(`intake-row-${idx}`)?.scrollIntoView({ behavior: "smooth", block: "center" }); }, 100);
      return;
    }
    try {
      const { data } = await api.get("/intake/barcode-lookup", { params: { code: c } });
      if (data.found && data.product_name) {
        let best = -1, score = 0;
        items.forEach((it, i) => { const s = similarity(it.name, data.product_name); if (s > score) { score = s; best = i; } });
        if (best >= 0 && score >= 0.34) {
          setItems(items.map((it, k) => (k === best ? { ...it, _sel: true, barcode: c } : it)));
          setScanHighlight(best);
          setScanResult({ ok: true, msg: `${data.product_name} → riga "${items[best].name}"` });
          setTimeout(() => { document.getElementById(`intake-row-${best}`)?.scrollIntoView({ behavior: "smooth", block: "center" }); }, 100);
          return;
        }
        setScanResult({ ok: false, msg: `"${data.product_name}" non è tra le righe del documento` });
        return;
      }
    } catch { /* noop */ }
    setScanResult({ ok: false, msg: `Barcode ${c} non trovato. Associa il codice a una riga a mano.` });
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const paid = opts.payMethod !== "sospeso";
      const body = {
        ...doc, taxable: parseFloat(doc.taxable) || 0, tax: parseFloat(doc.tax) || 0, total: parseFloat(doc.total) || 0,
        items: items.filter((it) => it._sel).map(({ _sel, lots, ...it }) => ({
          ...it, quantity: parseFloat(it.quantity) || 0, unit_price: parseFloat(it.unit_price) || 0, discount: parseFloat(it.discount) || 0,
          price: parseFloat(it.price) || 0, tax_rate: parseFloat(it.tax_rate) || 0,
          lots: (lots || []).filter((l) => l.lot_number || l.expiry_date || l.label_image_path).map(({ _confirmed, ...l }) => ({ ...l, quantity: parseFloat(l.quantity) || 0 })),
        })),
        add_inventory: opts.inv, add_accounting: opts.acc, add_haccp: opts.haccp, paid, payment_method: paid ? opts.payMethod : "fornitore",
        ledger: opts.ledger, tax_rate: opts.ledger === "ufficiale" ? parseFloat(opts.taxRate) || 0 : 0, conformity: opts.conformity,
      };
      const { data: res } = await api.post("/intake/commit-v2", body);
      const c = res.created || {};
      const parts = [];
      if (c.inventory) parts.push(`${c.inventory} prodotti a magazzino`);
      if (res.lots) parts.push(`${res.lots} lotti tracciati`);
      if (c.transactions) parts.push(`${c.transactions} spese`);
      if (c.haccp) parts.push("ricezione HACCP");
      toast.success("Carico confermato: " + (parts.join(" · ") || "fatto"));
      (res.warnings || []).forEach((w) => toast.warning(w));
      setOpen(false); reset(); onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCommitting(false); }
  };

  const isScontrino = docType === "scontrino";
  const sel = items.filter((it) => it._sel);
  const canNext = step === 0 ? (items.length > 0 || !!doc.supplier) : step === 1 ? sel.length > 0 : true;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full" data-testid={isScontrino ? "receipt-intake-button" : "invoice-intake-button"}>
          {isScontrino ? <Receipt className="w-4 h-4 mr-1" /> : <FileScan className="w-4 h-4 mr-1" />} {isScontrino ? "Scontrino" : "Ricezione da fattura"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl w-[96vw] max-h-[94vh] overflow-y-auto p-4 sm:p-6" data-testid="invoice-intake-dialog">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg">{step === 1 ? "Prodotti rilevati nel documento" : step === 2 ? "Acquisizione etichetta" : step === 3 ? "Conferma carico" : "Carico merce da fattura + tracciabilità AI"}</DialogTitle>
          <div className="flex items-center gap-1 pt-1" data-testid="intake-steps">
            {STEPS.map((s, i) => (
              <button key={s} type="button" onClick={() => (i <= maxStep || i < step) && setStep(i)} className={`text-[11px] px-2 py-0.5 rounded-full ${i === step ? "bg-primary text-primary-foreground" : i <= maxStep ? "bg-secondary text-foreground" : "bg-secondary/40 text-muted-foreground"}`} data-testid={`intake-step-${i}`}>{i + 1}. {s}</button>
            ))}
          </div>
          <DialogDescription className="sr-only">Procedura guidata di carico merce con tracciabilità</DialogDescription>
        </DialogHeader>

        {step === 0 && <StepDocument doc={doc} setDoc={setDoc} scanning={scanning} onFiles={scanFiles} hasItems={items.length > 0} />}
        {step === 1 && <StepProducts items={items} setItems={setItems} classes={classes} onCapture={openCapture} onQuickScan={quickScan} quickScanning={quickScanning} onOpenScanner={() => { setScanResult(null); setScanOpen(true); }} scanHighlight={scanHighlight} />}
        {step === 2 && capIdx !== null && items[capIdx] && (
          <LabelCapture item={items[capIdx]} token={token} onChange={(it) => setItems(items.map((x, k) => (k === capIdx ? it : x)))} onNext={goNextProduct} hasNext />
        )}
        {step === 2 && (capIdx === null || !items[capIdx]) && <p className="text-sm text-muted-foreground">Seleziona un prodotto dallo step Prodotti e premi "Acquisisci etichetta".</p>}
        {step === 3 && <StepSummary doc={doc} items={items} opts={opts} setOpts={setOpts} />}
        <BarcodeScanner open={scanOpen} onOpenChange={(o) => { setScanOpen(o); if (!o) setScanHighlight(null); }} onScan={onBarcodeScan} lastResult={scanResult} />

        <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
          {step > 0 ? <Button variant="ghost" className="rounded-full" onClick={() => setStep(step === 2 ? 1 : step - 1)} data-testid="intake-back"><ArrowLeft className="w-4 h-4 mr-1" /> Indietro</Button> : <span />}
          {step === 0 && items.length > 0 && <Button className="rounded-full" onClick={() => go(1)} data-testid="intake-next">Prodotti <ArrowRight className="w-4 h-4 ml-1" /></Button>}
          {step === 0 && !items.length && <Button variant="outline" className="rounded-full" onClick={() => { setItems([newItem({ quantity: 1 })]); go(1); }} data-testid="intake-manual">Inserisci righe a mano <ArrowRight className="w-4 h-4 ml-1" /></Button>}
          {step === 1 && (
            <div className="flex gap-2">
              {needIdx.some((i) => traceStatus(items[i]) !== "completa") && <Button className="rounded-full" onClick={() => openCapture(nextNeeding(-1))} disabled={!canNext} data-testid="intake-start-labels">Acquisisci etichette <ArrowRight className="w-4 h-4 ml-1" /></Button>}
              <Button variant={needIdx.some((i) => traceStatus(items[i]) !== "completa") ? "outline" : "default"} className="rounded-full" onClick={() => go(3)} disabled={!canNext} data-testid="intake-skip-labels">{needIdx.some((i) => traceStatus(items[i]) !== "completa") ? "Carica senza foto" : "Riepilogo"} <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </div>
          )}
          {step === 2 && <Button className="rounded-full" onClick={() => go(3)} data-testid="intake-to-summary">Riepilogo <ArrowRight className="w-4 h-4 ml-1" /></Button>}
          {step === 3 && <Button className="rounded-full" onClick={commit} disabled={committing || sel.length === 0} data-testid="intake-commit-button">{committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Conferma carico ({sel.length})</>}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
